import { useTranslation } from 'react-i18next';
import {
  ArrowRightIconOutlined,
} from '~/icons';
import { useStateVar } from '~/helpers/useStateVar';
import { useWebSocket } from '~/helpers/wsConnection';
import {
  ChangeEvent,
  MouseEvent, ReactNode, useEffect, useRef, useState,
} from 'react';
import { apiCall } from '~/providers';
import { getUserProfile, refreshUserProfile } from '~/helpers/userProfile';
import { Link, useHistory } from 'react-router-dom';
import {
  iconsMachineTypes, updateDamStatus, updateDriTelemetry, updateDutStatus,
} from './helpersIndex';
import { InputSearch } from '~/components';
import { getObjects } from '~/helpers/getObjetcs';
import Pagination from 'rc-pagination';
import {
  ContainerPage, SearchInputCommon, ContainerHeader, ImageHeader, TextOverlay, Title, Subtitle, LineStyle, ContainerBoxMachines, ContainerSemMaquinas, ContainerCardMachine, TitleMachine, ContainerSubTitles,
  ContainerPagination,
  LineSeparator,
  ContainerStatusCard,
  ContainerEnvironments,
  EnvironmentCard,
  EnvironmentTitle,
  EnvironmentDetails,
  TitleMachineSkeleton,
  HeaderWithSkeletonLoading,
  InputInnerContainer,
  InputWrapper,
  ContainerHeaderTitle,
  MachineSearchContainer,
  AvailableCountContainer,
} from './style';
import { OperationType, StatusCard, StatusType } from '../StatusCard';
import { formatNumberWithFractionDigits } from '~/helpers/thousandFormatNumber';
import Skeleton from 'react-loading-skeleton';
import {
  formatHealthIndexToStatus, verifyConnection, verifyOperation, verifyStatus,
} from '../MachineProfile/helpers';
import { ErrorMachines } from './components/ErrorsMachines';
import { OnboardingRemoteControlModal } from '~/components/ModalsRemoteControl/Onboarding';
import { UserSatisfactionSurveyModal } from '~/components/ModalsRemoteControl/UserSatisfactionSurvey';
import { SurveyName } from '~/providers/types/survey';
import { delay } from '~/helpers/delay';
import { State } from '../MachineProfile/types';
import Hotjar from '@hotjar/browser';

export type TMachine = {
  MACHINE_ID: number,
  MACHINE_NAME: string,
  MACHINE_TYPE: string,
  ECONOMY_ENABLED: boolean,
  SETPOINT: number,
  SETPOINT_EXCEPTION_ECO: number,
  OPERATION: string | null,
  DEV_AUT_CODE: string,
  H_INDEX: number,
  ENVIRONMENTS: {
    ENVIRONMENT_ID: number,
    ENVIRONMENT_NAME: string,
    ENVIRONMENT_ROOM_TYPE_NAME: string,
    DUT_CODE: string,
    SELF_REFERENCE?: boolean,
    RETURN_TEMPERATURE_FIELD?: 'Temperature' | 'Temperature_1' | 'TempAmb',
    Temperature?: number
  }[],
  RSSI?: number,
  status?: string,
  Mode?: string | null,
  useTelemetry: boolean
}

interface EnvironmentInfoProps {
  environment: {
    ENVIRONMENT_NAME: string;
    Temperature?: number;
    RETURN_TEMPERATURE_FIELD?: 'Temperature' | 'Temperature_1' | 'TempAmb';
  },
  setpoint: number | null,
  statusDevAut?: StatusType,
  operationDevAut?: OperationType,
  modeDevAut?: string,
  devAutIsConnected?: boolean,
  economyEnabled?: boolean
}

interface MachinesSearchProps {
  hasError: boolean;
  disabled: boolean;
  state: {
    searchText: string;
    machineFiltered: TMachine[];
  };
  onSearch: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const MachineListCR = (): JSX.Element => {
  const profile = getUserProfile();
  const wsConnRef = useRef<any>(null);
  const [state, render, setState] = useStateVar(() => {
    const state = {
      unitId: null as unknown as number,
      orderedFilter: [] as string[],
      machinesList: [] as TMachine[],
      selectedFilters: [] as string[],
      machineFiltered: [] as TMachine[],
      automaticMode: false,
      onTelmTimer: undefined as undefined | NodeJS.Timeout,
      hasNewTelm: false,
      CLIENT_NAME: '' as string,
      UNIT_NAME: '' as string,
      isLoading: true,
      searchText: '',
      currentPageList: [] as TMachine[],
      tablePagination: { tablePage: 1, tablePageSize: 9, totalItems: 0 },
      hasError: false,
      survey: undefined as {
        path: string,
        enabled: boolean,
      } | undefined,
      needSurvey: false,
    };
    return state;
  });
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const isDesktop = window.matchMedia('(min-width: 770px)').matches;

  function getUnitId() {
    const unitIds: number[] = [];
    profile.permissions.PER_UNIT?.forEach((item) => item.units.forEach((unit) => unitIds.push(unit)));
    setState({ unitId: unitIds[0] });
  }

  async function getSurveyInfo() {
    try {
      const responseSurvey = await apiCall('/survey/get-info', {
        NAME: SurveyName.REMOTE_CONTROL,
      });
      setState({
        survey: {
          enabled: responseSurvey.ENABLED,
          path: responseSurvey.PATH,
        },
      });
    } catch (error) {
      console.error(error);
      setState({ hasError: true });
    }
  }

  async function handleChangePage(page: number) {
    setState({ tablePagination: { ...state.tablePagination, tablePage: page } });
    render();
    getPage();
  }

  const getPage = () => {
    const start = (state.tablePagination.tablePage - 1) * state.tablePagination.tablePageSize;
    const end = start + state.tablePagination.tablePageSize;

    const currentMachinesPage = state.machineFiltered.slice(start, end);
    setState({ currentPageList: currentMachinesPage });
    render();
  };

  async function getMachinesData(): Promise<void> {
    setState({ isLoading: true });
    try {
      const responsePromise = apiCall('/remote-control/get-machines-list', { UNIT_ID: state.unitId });
      const delayPromise = delay(1200);

      const response = await responsePromise;
      await delayPromise;

      await getSurveyInfo();

      const machines = response.MACHINES.map((machine) => ({ ...machine, useTelemetry: false }));

      setState({
        machinesList: machines,
        machineFiltered: machines,
        tablePagination: { ...state.tablePagination, totalItems: response?.MACHINES?.length, tablePage: 1 },
      });
      getPage();
      setState({ CLIENT_NAME: response.CLIENT_NAME, UNIT_NAME: response.UNIT_NAME });
    } catch (err) {
      console.error(err);
      setState({ hasError: true });
    } finally {
      setState({ isLoading: false });
      onWsOpenAux();
    }
  }

  const onSearch = (e): void => {
    setState({ searchText: e.target.value });

    if (e.target.value) {
      const machinesFiltered = state.machinesList.filter((row) => getObjects(row, e.target.value).length);
      setState({ machineFiltered: machinesFiltered, tablePagination: { ...state.tablePagination, totalItems: machinesFiltered.length, tablePage: 1 } });
    } else {
      setState({ machineFiltered: state.machinesList, tablePagination: { ...state.tablePagination, totalItems: state.machinesList.length, tablePage: 1 } });
    }

    getPage();
  };

  useEffect(() => {
    getUnitId();
    getMachinesData();
  }, []);

  useWebSocket(onWsOpen, onWsMessage, beforeWsClose);

  function onWsOpen(wsConn) {
    wsConnRef.current = wsConn;
  }

  function onWsOpenAux() {
    if (wsConnRef.current) {
      wsConnRef.current.send({
        type: 'subscribeStatus',
        data: { unit_id: Number(state.unitId) },
      });
      wsConnRef.current.send({
        type: 'dutSubscribeRealTime',
        data: { UNIT_ID: Number(state.unitId) },
      });
      wsConnRef.current.send({
        type: 'damStatus',
        data: { UNIT_ID: Number(state.unitId) },
      });
      wsConnRef.current.send({
        type: 'driSubscribeRealTime',
        data: { UNIT_ID: Number(state.unitId) },
      });
    }
  }

  function onWsMessage(payload) {
    if (payload.type === 'dutAutTelemetry' || payload.type === 'dutStatus') {
      if (updateDutStatus(payload.data, state.machineFiltered, payload.type)) {
        updateDutStatus(payload.data, state.machinesList, payload.type);
        if (state.onTelmTimer) clearTimeout(state.onTelmTimer);
        state.hasNewTelm = true;
        state.onTelmTimer = setTimeout(() => state.hasNewTelm && render(), 100);
        render();
      }
    }
    if (payload.type === 'damStatus') {
      if (updateDamStatus(payload.data, state.machineFiltered, false)) {
        if (state.onTelmTimer) clearTimeout(state.onTelmTimer);
        updateDamStatus(payload.data, state.machinesList, false);
        state.hasNewTelm = true;
        state.onTelmTimer = setTimeout(() => state.hasNewTelm && render(), 100);
        render();
      }
    }
    if (payload.type === 'driTelemetry') {
      if (updateDriTelemetry(payload.data, state.machineFiltered)) {
        if (state.onTelmTimer) clearTimeout(state.onTelmTimer);
        updateDriTelemetry(payload.data, state.machinesList);
        state.hasNewTelm = true;
        state.onTelmTimer = setTimeout(() => state.hasNewTelm && render(), 100);
        render();
      }
    }
  }
  function beforeWsClose(wsConn) {
    wsConn.send({ type: 'unsubscribeStatus' });
    wsConn.send(JSON.stringify({ type: 'dutUnsubscribeRealTime', data: {} }));
    wsConn.send({ type: 'driUnsubscribeRealTime' });
  }

  function updateRemoteControlProfileKey(needUpdateSurvey?: boolean): void {
    const controlOnboarding: string[] = [];

    if (!profile.permissions.isPersonaII) {
      controlOnboarding.push('intro');
    }
    controlOnboarding.push('tour');

    if (needUpdateSurvey) {
      controlOnboarding.push('survey');
    }

    apiCall('/users/edit-user', {
      USER: profile?.user,
      PREFS: JSON.stringify({
        remoteControl: controlOnboarding,
      }),
    }).then((response) => {
      if (response) {
        refreshUserProfile();
      }
    });
  }

  async function registerFirstRemoteControlAccess(currentRemoteControlPrefs?: string[]): Promise<void> {
    try {
      const response = await apiCall('/users/edit-user', {
        USER: profile?.user,
        PREFS: JSON.stringify({
          remoteControl: [...(currentRemoteControlPrefs ?? []), 'accessed'],
        }),
      });
      if (response) {
        refreshUserProfile();
      }
    } catch (error) {
      console.error(error);
    }

    if (process.env.REACT_APP_HOTJAR_ID && process.env.REACT_APP_HOTJAR_VERSION) {
      Hotjar.event('onboarding_controle_remoto_iniciado');
    }

    try {
      await apiCall('/remote-control/register-onboarding-access', {});
    } catch (error) {
      console.error(error);
    }
  }

  function onCloseOnboardingModal(): void {
    updateRemoteControlProfileKey();

    setShowOnboardingModal(false);

    if (state.needSurvey && state.survey?.enabled) {
      setState({ needSurvey: false });
      setShowSurveyModal(true);
    }
  }

  function onCloseSurveyModal(): void {
    setShowSurveyModal(false);
  }

  function onClickRateSurveyModal(): void {
    updateRemoteControlProfileKey(true);
    onCloseSurveyModal();

    if (state.survey?.path) {
      window.open(state.survey.path, '_blank');
    }
  }

  useEffect(() => {
    if (profile.permissions.isPersonaII) {
      const parsedPrefs = JSON.parse(profile?.prefs ?? '{}');
      const introRemoteControl = parsedPrefs.remoteControl;
      const needSurvey = !introRemoteControl?.includes('survey');
      setState({
        needSurvey,
      });

      if (!introRemoteControl?.includes('tour')) {
        setTimeout(() => {
          setShowOnboardingModal(true);
        }, 500);
      }

      if (!introRemoteControl?.includes('accessed')) {
        registerFirstRemoteControlAccess(introRemoteControl);
      }
    }
  }, []);

  useEffect(() => {
    const parsedPrefs = JSON.parse(profile?.prefs ?? '{}');
    const introRemoteControl = parsedPrefs.remoteControl;

    if (!!state.survey?.enabled && state.needSurvey && introRemoteControl?.includes('tour')) {
      setShowSurveyModal(true);
    }
  }, [state.survey]);

  return (
    <>
      <UserSatisfactionSurveyModal
        isOpen={showSurveyModal}
        onClose={onCloseSurveyModal}
        onClickRate={onClickRateSurveyModal}
      />
      <OnboardingRemoteControlModal
        isOpen={showOnboardingModal}
        onClose={onCloseOnboardingModal}
      />
      <ContainerPage isMobile={!isDesktop}>

        {!state.hasError && !state.isLoading && (
          <HeaderUnit UNIT_NAME={state.UNIT_NAME} CLIENT_NAME={state.CLIENT_NAME} />
        )}

        {state.isLoading && (
          <ContainerHeader isMobile={!isDesktop} data-testid="containerHeaderSkeleton">
            <Skeleton width="100%" height={isDesktop ? 200 : 140} borderRadius={8} />
            <HeaderWithSkeletonLoading />
          </ContainerHeader>
        )}

        <MachinesSearch
          state={state}
          onSearch={onSearch}
          hasError={state.hasError}
          disabled={state.isLoading}
        />

        {state.isLoading ? (
          <ContainerBoxMachines isMobile={!isDesktop} haveData style={{ paddingBottom: '20px' }} data-testid="containerMachinesSkeleton">
            {Array.from({ length: 9 }).map((_, index) => (
              <CardMachineSkeleton key={index} />
            ))}
          </ContainerBoxMachines>
        ) : (
          <>
            {state.hasError ? (
              <ErrorMachines />
            ) : (
              <>
                <BoxCardsMachines MACHINES={state.currentPageList} />
                {state.currentPageList.length > 0 && (
                  <ContainerPagination>
                    <Pagination
                      className="ant-pagination"
                      current={state.tablePagination.tablePage}
                      total={state.tablePagination.totalItems}
                      pageSize={state.tablePagination.tablePageSize}
                      onChange={(current) => handleChangePage(current)}
                      disabled={false}
                      showTitle={false}
                    />
                  </ContainerPagination>
                )}
              </>
            )}
          </>
        )}
      </ContainerPage>
    </>
  );
};

const AvailableCount = ({ count }: { count: number }) => {
  const { t } = useTranslation();

  if (count === 0) return null;

  return (
    <AvailableCountContainer>
      <span style={{ color: '#A5A7B0', fontSize: 14 }}>
        {count === 1
          ? t('maquinaDisponivel', { count })
          : t('maquinasDisponiveis', { count })}
      </span>
    </AvailableCountContainer>
  );
};

function MachinesSearch({
  state, onSearch, hasError, disabled,
}: Readonly<MachinesSearchProps>) {
  const { t } = useTranslation();

  return (
    <MachineSearchContainer hasError={hasError}>
      <ContainerHeaderTitle>
        <h2>{t('maquinas')}</h2>
        {!disabled && (
          <AvailableCount count={state?.machineFiltered?.length} />
        )}
      </ContainerHeaderTitle>
      {!hasError && (
        <InputWrapper isDisabled={disabled}>
          <InputInnerContainer>
            <SearchInputCommon
              isDisabled={disabled}
              style={{ margin: 0 }}
            >
              <InputSearch
                id="search"
                name="search"
                placeholder={t('buscarMaquinasAmbientes')}
                value={state.searchText}
                onChange={onSearch}
                disabled={disabled}
                style={{ margin: 0 }}
                isIconInFront
                keepPlaceholderOnly
              />
            </SearchInputCommon>
          </InputInnerContainer>
        </InputWrapper>
      )}
    </MachineSearchContainer>
  );
}

function HeaderUnit({ UNIT_NAME, CLIENT_NAME }) {
  return (
    <ContainerHeader data-testid="containerHeaderUnit">
      <ImageHeader />
      <TextOverlay>
        <Title>{CLIENT_NAME}</Title>
        <Subtitle>{UNIT_NAME}</Subtitle>
      </TextOverlay>
      <LineStyle />
    </ContainerHeader>
  );
}

function BoxCardsMachines({ MACHINES }: Readonly<{ MACHINES: TMachine[] }>) {
  const isDesktop = window.matchMedia('(min-width: 770px)').matches;
  const { t } = useTranslation();

  return (
    <ContainerBoxMachines
      isMobile={!isDesktop}
      haveData={MACHINES.length > 0}
      style={{
        boxShadow: MACHINES.length ? 'none' : '0px 0px 4px 0px rgba(0, 0, 0, 0.15)',
      }}
    >
      {MACHINES.map((machine) => (
        <CardMachine key={machine.MACHINE_NAME} MACHINE={machine} />
      ))}
      {!MACHINES.length && (
        <ContainerSemMaquinas>
          <span>{t('maquinasNaoDisponiveisExibicao')}</span>
        </ContainerSemMaquinas>
      )}
    </ContainerBoxMachines>
  );
}

function CardMachine({ MACHINE }: Readonly<{
  MACHINE: TMachine
}>) {
  const history = useHistory();

  return (
    <div
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        const isButtonClick = !!(event.target as HTMLElement | undefined)?.closest('button');
        if (isButtonClick) return;

        history.push(`/controle-inteligente/${MACHINE.MACHINE_ID}`, {
          MACHINE_NAME: MACHINE.MACHINE_NAME,
          environmentName: MACHINE.ENVIRONMENTS.length > 0 ? MACHINE.ENVIRONMENTS[0].ENVIRONMENT_NAME : '',
          deviceCode: MACHINE.DEV_AUT_CODE,
        });
      }}
      style={{ cursor: 'pointer' }}
    >
      <CardMachineInfos MACHINE={MACHINE} />
    </div>
  );
}

function returnIconMachine(type: string): ReactNode {
  if (type && iconsMachineTypes[type]) {
    return iconsMachineTypes[type];
  }
  return iconsMachineTypes.semMonit;
}

function CardMachineInfos({ MACHINE }: Readonly<{
  MACHINE: TMachine
}>) {
  const isDamAut = MACHINE.DEV_AUT_CODE?.startsWith('DAM');
  const setpoint = isDamAut && !MACHINE.ECONOMY_ENABLED ? MACHINE.SETPOINT_EXCEPTION_ECO : MACHINE.SETPOINT;

  const machine = {
    operation: MACHINE.OPERATION as State | null,
    rssi: MACHINE.RSSI,
    status: MACHINE.status as 'ONLINE' | 'OFFLINE',
  };

  const operation = verifyOperation(machine);
  const status = verifyStatus(machine, isDamAut);

  return (
    <ContainerCardMachine aria-label="card-machines">
      <TitleMachine>
        <div style={{ padding: '5px 5px 5px 0px' }}>
          {returnIconMachine(MACHINE.MACHINE_TYPE)}
        </div>
        <span style={{ fontSize: '12px' }}>{MACHINE.MACHINE_NAME}</span>
        <Link
          to={{
            pathname: `/controle-inteligente/${MACHINE.MACHINE_ID}`,
            state: {
              MACHINE_NAME: MACHINE.MACHINE_NAME,
              MACHINE_TYPE: MACHINE.MACHINE_TYPE,
              deviceCode: MACHINE.DEV_AUT_CODE,
            },
          }}
          style={{ padding: 2, color: 'black' }}
        >
          <ArrowRightIconOutlined width={24} height={24} />
        </Link>
      </TitleMachine>
      <ContainerSubTitles>
        <ContainerEnvironments>
          {MACHINE.ENVIRONMENTS.map((environment) => (
            <EnvironmentInfo
              key={environment.ENVIRONMENT_ID}
              environment={environment}
              setpoint={setpoint}
              statusDevAut={status}
              operationDevAut={operation}
              modeDevAut={MACHINE.Mode ?? undefined}
              devAutIsConnected={verifyConnection(MACHINE.RSSI, MACHINE.status as 'ONLINE' | 'OFFLINE')}
              economyEnabled={MACHINE.ECONOMY_ENABLED}
            />
          ))}
          <LineSeparator />
          <ContainerStatusCard>
            <StatusCard
              isConnected={verifyConnection(MACHINE.RSSI, MACHINE.status as 'ONLINE' | 'OFFLINE')}
              operation={operation}
              status={status}
              healthStatus={formatHealthIndexToStatus(MACHINE.H_INDEX)}
              isListScreen
            />
          </ContainerStatusCard>
        </ContainerEnvironments>
      </ContainerSubTitles>
    </ContainerCardMachine>
  );
}

const EnvironmentInfo = ({
  environment,
  setpoint,
  statusDevAut,
  operationDevAut,
  modeDevAut,
  devAutIsConnected,
  economyEnabled,
}: EnvironmentInfoProps) => {
  const { t } = useTranslation();

  function verifySetpoint() {
    if (!devAutIsConnected || !setpoint) return '-';
    let displaySetpoint = `${formatNumberWithFractionDigits(setpoint, { minimum: 0, maximum: 0 })} °C`;
    if (statusDevAut === 'freeMode') {
      displaySetpoint = '-';
    } else if (operationDevAut === 'ventilating') {
      if (modeDevAut?.toLocaleLowerCase() === 'manual' || !economyEnabled) {
        displaySetpoint = '-';
      }
    }

    return displaySetpoint;
  }

  return (
    <EnvironmentCard>
      <EnvironmentTitle>{environment.ENVIRONMENT_NAME}</EnvironmentTitle>
      <EnvironmentDetails>
        {environment.RETURN_TEMPERATURE_FIELD ? (
          <div>
            <span>{t('ambiente')}</span>
            <span>{environment.Temperature ? `${formatNumberWithFractionDigits(environment.Temperature ?? 0, { maximum: 0, minimum: 0 })} °C` : '-'}</span>
          </div>
        ) : null}
        <div>
          <span>{t('desejada')}</span>
          <span>{verifySetpoint()}</span>
        </div>
      </EnvironmentDetails>
    </EnvironmentCard>
  );
};

const CardMachineSkeleton = () => (
  <ContainerCardMachine>
    <TitleMachineSkeleton>
      <Skeleton height={36} width={36} borderRadius={0} />
      <div>
        <Skeleton height={12} width="100%" count={2} borderRadius={100} />
      </div>
      <Skeleton height={36} width={36} borderRadius={0} />
    </TitleMachineSkeleton>

    <ContainerSubTitles>
      <div style={{
        display: 'flex', justifyContent: 'space-between', width: '100%', margin: '20px 0px',
      }}
      >
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8, width: '40%',
        }}
        >
          <Skeleton height={12} count={2} />
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8, width: '40%',
        }}
        >
          <Skeleton height={12} count={2} />
        </div>
      </div>

    </ContainerSubTitles>
    <LineSeparator />

    <ContainerStatusCard>
      <StatusCard isListScreen isLoading isConnected={false} />
    </ContainerStatusCard>

  </ContainerCardMachine>
);
